import { OverflowTooltip } from '@backstage/core-components';
import { SimpleScore } from '../types/Scores';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { Divider, Grid, Typography, useTheme } from '@material-ui/core';
import { green, orange, red } from '@material-ui/core/colors';

const TrendIndicator = ({
  delta,
  higherIsBetter = false,
  timeRange,
}: {
  delta: number;
  higherIsBetter?: boolean;
  timeRange?: string;
}) => {
  const theme = useTheme();
  const isGood = higherIsBetter ? delta > 0 : delta < 0;
  const isBad = higherIsBetter ? delta < 0 : delta > 0;
  let color = theme.palette.text.primary;
  if (isGood) color = green[400];
  else if (isBad) color = red[300];

  return (
    <Grid container direction="column" alignItems="center" spacing={0}>
      <Grid item>
        <Typography
          variant="caption"
          style={{
            color,
            fontWeight: theme.typography.fontWeightBold,
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          {delta < 0 && (
            <TrendingDownIcon
              fontSize="small"
              style={{ color: isGood ? green[400] : red[300] }}
            />
          )}
          {delta > 0 && (
            <TrendingUpIcon
              fontSize="small"
              style={{ color: isGood ? green[400] : red[300] }}
            />
          )}
          {delta === 0 && (
            <TrendingFlatIcon
              fontSize="small"
              style={{ color: theme.palette.text.primary }}
            />
          )}
          {delta > 0 ? `+${delta}` : delta.toString()}
        </Typography>
      </Grid>
      {timeRange && (
        <Grid item>
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            vs {timeRange}d ago
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export const HealthScoreCard = ({
  score,
  scoreRules,
  trendInfo = { enabled: false },
}: {
  score: SimpleScore;
  scoreRules: any;
  trendInfo?:
    | { enabled: false }
    | { enabled: true; previousScore: SimpleScore; timeRange: string };
}) => {
  const theme = useTheme();

  const trendDeltaRed = trendInfo?.enabled
    ? score.red - trendInfo.previousScore.red
    : 0;

  return (
    <Grid container direction="column" spacing={0}>
      {/* Need Attention */}
      <Grid
        item
        style={{ textAlign: 'center', width: '100%', paddingBottom: '2px' }}
      >
        <Typography
          variant="caption"
          style={{
            fontWeight: theme.typography.fontWeightBold,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: red[300],
          }}
        >
          Need Attention
        </Typography>
      </Grid>

      <Grid item style={{ textAlign: 'center', width: '100%' }}>
        <Typography
          variant="h3"
          style={{
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: 1.1,
            color: red[300],
          }}
        >
          <OverflowTooltip
            title={scoreRules.red.info}
            text={score.red.toString()}
            line={1}
          />
        </Typography>
      </Grid>

      {trendInfo?.enabled && (
        <Grid item style={{ display: 'flex', justifyContent: 'center' }}>
          <TrendIndicator
            delta={trendDeltaRed}
            higherIsBetter={false}
            timeRange={trendInfo.timeRange}
          />
        </Grid>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* Advisory | Healthy — side by side */}
      <Grid item container style={{ paddingTop: '4px' }}>
        {/* Advisory */}
        <Grid
          item
          xs={6}
          container
          direction="column"
          alignItems="center"
          spacing={0}
          style={{
            borderRight: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item style={{ textAlign: 'center' }}>
            <Typography
              variant="caption"
              style={{
                fontWeight: theme.typography.fontWeightBold,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: orange[300],
              }}
            >
              Advisory
            </Typography>
          </Grid>
          <Grid item style={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              style={{
                fontWeight: theme.typography.fontWeightBold,
                lineHeight: 1.1,
                color: orange[300],
              }}
            >
              <OverflowTooltip
                title={scoreRules.orange.info}
                text={score.orange.toString()}
                line={1}
              />
            </Typography>
          </Grid>
        </Grid>

        {/* Healthy */}
        <Grid
          item
          xs={6}
          container
          direction="column"
          alignItems="center"
          spacing={0}
        >
          <Grid item style={{ textAlign: 'center' }}>
            <Typography
              variant="caption"
              style={{
                fontWeight: theme.typography.fontWeightBold,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: green[400],
              }}
            >
              Healthy
            </Typography>
          </Grid>
          <Grid item style={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              style={{
                fontWeight: theme.typography.fontWeightBold,
                lineHeight: 1.1,
                color: green[400],
              }}
            >
              <OverflowTooltip
                title={scoreRules.green.info}
                text={score.green.toString()}
                line={1}
              />
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Divider style={{ margin: '12px 0' }} />

      {/* Total Count */}
      <Grid
        item
        container
        alignItems="center"
        justifyContent="space-between"
        style={{
          paddingLeft: '12px',
          paddingRight: '12px',
          paddingTop: '4px',
        }}
      >
        <Grid item>
          <Typography
            variant="caption"
            style={{
              fontWeight: theme.typography.fontWeightBold,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: theme.palette.text.secondary,
            }}
          >
            Total Count
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            variant="caption"
            style={{ fontWeight: theme.typography.fontWeightBold }}
          >
            {score.red + score.orange + score.green}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};
